import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import { Button, Layout, List, Row, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { hasHistory } from '@app/services/hasHistory';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { HeaderTitle } from '@components/HeaderTitle';
import { ListItem } from '@components/ListItem';
import { UserStatus } from '@components/Status/components/UserStatus';

import { Assignments } from '../../component/Assignments';
import { useEmployee } from '../../providers';

import { Container } from './styles';

export const SingleEmployee: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const commonT = useTranslation(NSKeys.common);
  const pageT = useTranslation(NSKeys.employees);
  const assignmentsT = useTranslation(NSKeys.assignments);
  const employee = useEmployee();

  const currentUser = useLocalSelector((state) => state.currentSession.data);

  const canEditProfile = !!employee.data?.$permissions?.update;
  const canViewAssignments = currentUser?.scope?.$permissions?.view_assignments === true;
  const canOpenEdit = canEditProfile || canViewAssignments;

  const goBack = () => (hasHistory() ? navigate(-1) : navigate('/dashboard/employees'));

  return (
    <Layout>
      <Layout.Header>
        <Breadcrumbs
          apiTitles={[{ id: ':employeeId', value: `${employee.data?.last_name} ${employee.data?.first_name}` }]}
        />
        <Row justify="space-between" align="middle">
          <HeaderTitle
            title={`${employee.data?.last_name} ${employee.data?.first_name}`}
            onClick={goBack}
            loading={employee.loading}
          />
          <Space size="middle">
            {canOpenEdit && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/dashboard/employees/edit/${params?.id}`)}
                size="middle"
                type="primary">
                {commonT.t('edit')}
              </Button>
            )}
          </Space>
        </Row>
      </Layout.Header>
      <Layout.Content>
        <Container>
          <List style={{ maxWidth: 600, margin: '28px 0' }} loading={employee.loading}>
            <ListItem title={pageT.t('form.surnameLabel')} description={employee.data?.last_name} />
            <ListItem title={pageT.t('form.nameLabel')} description={employee.data?.first_name} />
            <ListItem title={pageT.t('form.emailLabel')} description={employee.data?.email} />
            <ListItem title={pageT.t('form.phoneLabel')} description={employee.data?.phone} />
            <ListItem title={pageT.t('form.jobtitleLabel')} description={employee.data?.title} />
            <ListItem title={pageT.t('tableColumns.status')} description=" ">
              <UserStatus isActive={employee.data?.is_active} />
            </ListItem>
            <ListItem title={assignmentsT.t('tags.fieldLabel')} description=" ">
              <Space size={10} wrap>
                {employee.data?.tags?.map((tag) => (
                  <Tag key={tag.uid} style={{ margin: 0 }}>
                    {tag.title}
                  </Tag>
                ))}
              </Space>
            </ListItem>
          </List>
        </Container>
        {canViewAssignments && employee.data && <Assignments employee={employee.data} readOnly />}
      </Layout.Content>
    </Layout>
  );
};
